import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { TextInput, type TextInputProps } from '../primitives/TextInput';
import { InputGroup } from '../primitives/InputGroup';
import { Icon } from '../primitives/Icon';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction } from './reactions';
import { useReactions } from './useReactions';
import { withInputAddons, type InputAddonProps } from './inputAddons';
import type { RhfLayoutProps } from './rhfLayout';
import type { EditPrecisionConfig } from './editPrecision';
import RhfInputTextField from './RhfInputTextField';

export interface RhfInputTextProps
  extends Omit<TextInputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'error'>,
    InputAddonProps,
    RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  /**
   * Synchronous input formatter applied on every `onChange` keystroke.
   * Receives the new raw value AND the previous formatted value so that
   * formatters can detect deletion direction and avoid re-inserting
   * separators (e.g. SSN dashes) that the user is trying to backspace through.
   */
  formatter?: (value: string, prevValue: string) => string;
  reactions?: Reaction[];
  editPrecision?: EditPrecisionConfig;
}

export const RhfInputText = React.forwardRef<HTMLInputElement, RhfInputTextProps>(
  function RhfInputText(
    {
      control,
      name,
      label,
      hint,
      required = false,
      semanticId,
      disabled,
      id,
      formatter,
      reactions,
      type,
      startAddon,
      endAddon,
      controlWidth,
      editPrecision,
      ...rest
    },
    ref,
  ) {
    const controlId = id ?? name;
    const overrides = useReactions(control, reactions);
    const [revealed, setRevealed] = React.useReducer((s: boolean) => !s, false);

    const isPassword = type === 'password';
    const resolvedType = isPassword && revealed ? 'text' : type;

    const finalDisabled = overrides.disabled ?? disabled ?? false;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;
    const finalPlaceholder = (overrides.placeholder as string) ?? rest.placeholder;

    if (overrides.visible === false) return null;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          if (editPrecision) {
            return (
              <RhfInputTextField
                field={field}
                controlId={controlId}
                label={finalLabel}
                hint={hint}
                required={finalRequired}
                semanticId={semanticId}
                disabled={finalDisabled}
                errorText={rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted })}
                formatter={formatter}
                controlWidth={controlWidth}
                editPrecision={editPrecision}
                inputRef={ref}
                startAddon={startAddon}
                endAddon={endAddon}
                placeholder={finalPlaceholder}
                {...rest}
              />
            );
          }

          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const input = (
            <TextInput
              ref={composeRefs(field.ref, ref)}
              id={controlId}
              name={field.name}
              value={field.value ?? ''}
              type={resolvedType}
              onChange={
                formatter
                  ? (e: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(formatter(e.target.value, field.value ?? ''))
                  : field.onChange
              }
              onBlur={field.onBlur}
              disabled={finalDisabled}
              error={Boolean(errorText)}
              semanticId={semanticId}
              placeholder={finalPlaceholder}
              autoComplete={isPassword ? 'off' : rest.autoComplete}
              {...rest}
            />
          );

          // Password toggle always uses InputGroup.Addon (icon, interactive).
          // startAddon/endAddon use InputGroup.AttachedAddon (text labels).
          // Both can coexist: startAddon + input + endAddon + password-toggle.
          const hasAddons = startAddon || endAddon || isPassword;

          const wrapped = hasAddons ? (
            <InputGroup error={Boolean(errorText)} disabled={finalDisabled}>
              {startAddon && (
                <InputGroup.AttachedAddon position="start">{startAddon}</InputGroup.AttachedAddon>
              )}
              {input}
              {endAddon && (
                <InputGroup.AttachedAddon position="end">{endAddon}</InputGroup.AttachedAddon>
              )}
              {isPassword && (
                <InputGroup.Addon
                  position="end"
                  interactive
                  onClick={setRevealed}
                  role="button"
                  aria-label={revealed ? 'Hide value' : 'Reveal value'}
                >
                  <Icon name={revealed ? 'visibility' : 'visibility_off'} size="sm" />
                </InputGroup.Addon>
              )}
            </InputGroup>
          ) : (
            input
          );

          return (
            <FormField
              label={finalLabel}
              htmlFor={controlId}
              hint={hint}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              {wrapped}
            </FormField>
          );
        }}
      />
    );
  },
);

RhfInputText.displayName = 'RhfInputText';
